/**
 * FANTASY FOOTBALL PAGE V5 - MATCHES CREATE MARKET DESIGN
 * 
 * Looks like the prediction market creation page.
 * Same gradient borders, AI conversation style, progress indicators.
 */

import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowLeft,
  Trophy,
  Loader,
  CheckCircle,
  Send,
  Sparkles,
  Brain,
  Zap,
  Target,
  Clock,
  Users,
  DollarSign,
  ChevronRight,
  ChevronDown,
  Search,
  Info,
  Settings
} from 'lucide-react'
import toast from 'react-hot-toast'
import axios from 'axios'
import { API_BASE_URL } from '../constants'
import { useAuthStore } from '../store/authStore'

// ============================================================================
// TYPES
// ============================================================================

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  questions?: Question[]
  contestPlan?: any
  searchResults?: any
  validationPlan?: any
}

interface Question {
  question: string
  type?: string
  suggestions?: string[]
  options?: string[]
  placeholder?: string
  required?: boolean
  category?: string
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function FantasyFootballPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  
  // State
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [conversationHistory, setConversationHistory] = useState<any[]>([])
  const [contestPlan, setContestPlan] = useState<any>(null)
  const [currentQuestions, setCurrentQuestions] = useState<Question[]>([])
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [step, setStep] = useState(1) // 1=describe, 2=refine, 3=create (legacy, now using phase)
  const [originalQuestion, setOriginalQuestion] = useState('') // Store the first question
  const [cachedGameData, setCachedGameData] = useState<any>(null) // Store Perplexity game data
  const [sessionId, setSessionId] = useState<string | null>(null) // Database session ID for persistence
  const [contestType, setContestType] = useState<'dfs' | 'survivor' | null>(null) // MUST SELECT BEFORE TYPING
  
  // NEW: 5-Phase Wizard State
  const [currentPhase, setCurrentPhase] = useState<1 | 2 | 3 | 4 | 5>(1) // 1=Games, 2=Scoring, 3=Rules, 4=Theme, 5=Review
  const [phaseConfirmed, setPhaseConfirmed] = useState<Record<number, boolean>>({1: false, 2: false, 3: false, 4: false})
  const [phaseMessages, setPhaseMessages] = useState<Record<number, Message[]>>({1: [], 2: [], 3: [], 4: [], 5: []})
  
  // AI Interpreted Summary - NOT raw user answers, but AI's interpretation of what the user wants
  // This is what shows in the sidebar
  const [aiInterpretedSummary, setAiInterpretedSummary] = useState<Record<string, string>>({})
  
  // Phase definitions
  const phases = [
    { num: 1, name: 'Games', icon: '🏈', description: 'Select which NFL games' },
    { num: 2, name: 'Scoring', icon: '📊', description: 'Strategy & scoring rules' },
    { num: 3, name: 'Rules', icon: '🏆', description: 'How users win & prizes' },
    { num: 4, name: 'Theme', icon: '🎨', description: 'Creative names & themes' },
    { num: 5, name: 'Review', icon: '✅', description: 'Confirm & create' }
  ]
  
  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])
  
  // Auto-focus input
  useEffect(() => {
    inputRef.current?.focus()
  }, [])
  
  // ============================================================================
  // SEND MESSAGE
  // ============================================================================
  
  const sendMessage = async (messageText?: string) => {
    // 🚨 PREVENT DOUBLE SUBMISSION - if already loading, ignore
    if (isLoading) {
      console.log('⚠️ BLOCKED: Already loading, ignoring duplicate submission');
      return;
    }
    
    const text = messageText || input.trim()
    if (!text && Object.keys(answers).length === 0) return
    
    const combinedAnswers = { ...answers }
    if (text) combinedAnswers.userMessage = text
    
    // If this is the first message, save it as the original question
    const isFirstMessage = messages.length === 0 || !originalQuestion
    if (isFirstMessage && text) {
      setOriginalQuestion(text)
    }
    
    // Add user message
    if (text) {
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'user',
        content: text
      }])
    } else if (Object.keys(answers).length > 0) {
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'user',
        content: Object.values(answers).join('\n')
      }])
    }
    
    setInput('')
    // DON'T clear answers yet - keep showing selection during loading
    // setAnswers({}) - moved to after response received
    setCurrentQuestions([])
    setIsLoading(true)
    setStep(2)
    
    // Use original question for continuity, or the new text if it's a new question
    const questionToSend = text || originalQuestion || 'Continue with my selections'
    
    // Build updated conversation history BEFORE sending (include current questions + answers)
    const updatedHistory = [...conversationHistory]
    if (currentQuestions.length > 0 && Object.keys(combinedAnswers).length > 0) {
      updatedHistory.push({
        questions: currentQuestions,
        answers: combinedAnswers
      })
    }
    
    // 🚨 DEBUG: Log what we're sending to help diagnose issues
    console.log('🚀 SENDING TO BACKEND:')
    console.log('   Question:', questionToSend)
    console.log('   Combined Answers:', combinedAnswers)
    console.log('   📋 CURRENT answers state keys:', Object.keys(answers))
    console.log('   📋 combinedAnswers keys:', Object.keys(combinedAnswers))
    console.log('   Conversation History Length:', updatedHistory.length)
    console.log('   Conversation History:', JSON.stringify(updatedHistory, null, 2))
    console.log('   Cached Game Data:', cachedGameData ? 'YES' : 'NO')
    
    try {
      // 🚨 Send sessionId, contestType, AND currentPhase for persistent database storage
      // contestType is EXPLICIT from user selection - not auto-detected!
      console.log('🏷️ CONTEST TYPE (user selected):', contestType)
      console.log('📍 CURRENT PHASE:', currentPhase, phases[currentPhase - 1]?.name || 'Unknown')
      const response = await axios.post(`${API_BASE_URL}/api/fantasy/analyze`, {
        contestType, // EXPLICIT: 'dfs' or 'survivor' - forces correct questions
        question: questionToSend,
        answers: Object.keys(combinedAnswers).length > 0 ? combinedAnswers : null,
        sessionId: sessionId,  // Database session for persistence
        userId: null,          // TODO: Add user auth
        currentPhase,          // NEW: Which phase we're in (1-5)
        phaseConfirmed         // NEW: Which phases are already confirmed
      }, {
        timeout: 36000000 // 10 hours timeout to match server (prevents frontend 504/timeout)
      })
      
      const result = response.data
      
      // 🚨 SAVE SESSION ID FROM DATABASE
      if (result.sessionId) {
        setSessionId(result.sessionId)
        console.log('💾 SESSION STORED IN DATABASE:', result.sessionId)
        console.log('   Total turns:', result.totalTurns)
        console.log('   All accumulated answers:', result.allAccumulatedAnswers)
      }
      
      // Update conversation history to match what we sent
      setConversationHistory(updatedHistory)
      
      // Cache game data from first Perplexity search (don't re-search on follow-ups)
      if (result.gameAnalysis && !cachedGameData) {
        console.log('💾 Caching game data from Perplexity search')
        setCachedGameData(result.gameAnalysis)
      }
      if (result.marketData && !cachedGameData) {
        console.log('💾 Caching market data from Perplexity search')
        setCachedGameData(result.marketData)
      }
      
      // 🎯 EXTRACT AI INTERPRETED SUMMARY - This is what shows in the sidebar!
      // NOT raw user answers, but the AI's interpretation of what they want
      // PRIORITY: understoodSoFar (always present) > contestPlan (final) > gameAnalysis
      
      setAiInterpretedSummary(prev => {
        const updated = { ...prev };
        
        // 🚨 PRIMARY SOURCE: understoodSoFar - AI sends this EVERY response with what it understood
        if (result.understoodSoFar) {
          const usf = result.understoodSoFar;
          if (usf.games && usf.games !== 'null') updated['Games'] = usf.games;
          if (usf.scoring && usf.scoring !== 'null') updated['Scoring'] = usf.scoring;
          if (usf.roster && usf.roster !== 'null') updated['Roster'] = usf.roster;
          if (usf.salaryCap && usf.salaryCap !== 'null') updated['Salary Cap'] = usf.salaryCap;
          if (usf.locks && usf.locks !== 'null') updated['Locks'] = usf.locks;
          if (usf.entryFee && usf.entryFee !== 'null') updated['Entry Fee'] = usf.entryFee;
          if (usf.prizes && usf.prizes !== 'null') updated['Prizes'] = usf.prizes;
          if (usf.theme && usf.theme !== 'null') updated['Theme'] = usf.theme;
          console.log('📋 Sidebar updated from understoodSoFar:', usf);
        }
        
        // SECONDARY: From gameAnalysis (games found)
        if (result.gameAnalysis?.gamesFound?.length > 0 && !updated['Games']) {
          const games = result.gameAnalysis.gamesFound;
          updated['Games'] = games.map((g: any) => `${g.awayTeam} @ ${g.homeTeam}`).join(', ');
        }
        
        // TERTIARY: From contestPlan (when ready_for_validation)
        if (result.contestPlan) {
          const plan = result.contestPlan;
          if (plan.gameSelection?.games && !updated['Games']) {
            updated['Games'] = plan.gameSelection.games.join(', ');
          }
          if (plan.scoring?.description && !updated['Scoring']) {
            updated['Scoring'] = plan.scoring.description;
          }
          if (plan.rosterFormat && !updated['Roster']) {
            updated['Roster'] = typeof plan.rosterFormat === 'string' ? plan.rosterFormat : JSON.stringify(plan.rosterFormat);
          }
          if (plan.salaryCap && !updated['Salary Cap']) {
            updated['Salary Cap'] = `$${plan.salaryCap.toLocaleString()}`;
          }
          if (plan.locking && !updated['Locks']) {
            updated['Locks'] = plan.locking;
          }
          if (plan.entryFee !== undefined && !updated['Entry Fee']) {
            updated['Entry Fee'] = plan.entryFee === 0 ? 'Free' : `$${plan.entryFee}`;
          }
          if (plan.prizeStructure?.description && !updated['Prizes']) {
            updated['Prizes'] = plan.prizeStructure.description;
          }
          if (plan.title && !updated['Title']) {
            updated['Title'] = plan.title;
          }
        }
        
        console.log('📋 AI Interpreted Summary updated:', updated);
        return updated;
      });
      
      if (result.status === 'needs_clarification') {
        // 🚨 DEBUG: Log what AI returned for sidebar
        console.log('🎯 AI RESPONSE understoodSoFar:', result.understoodSoFar);
        console.log('🎯 AI RESPONSE contestPlan:', result.contestPlan ? 'YES' : 'NO');
        
        let aiContent = result.interpretation || ''
        if (result.aiInsight) aiContent = result.aiInsight + (aiContent ? '\n\n' + aiContent : '')
        if (result.issue) aiContent = result.issue + (aiContent ? '\n\n' + aiContent : '')
        
        // NEW: Check if AI is telling us to advance to a new phase
        if (result.suggestedPhase && result.suggestedPhase > currentPhase) {
          // Confirm current phase and advance (cap at 5 to prevent overflow)
          setPhaseConfirmed(prev => ({ ...prev, [currentPhase]: true }))
          const nextPhase = Math.min(result.suggestedPhase || currentPhase + 1, 5) as 1|2|3|4|5
          setCurrentPhase(nextPhase)
        }
        
        // Sidebar now shows accumulated choices, so don't clutter the chat with them
        const newMessage = {
          id: Date.now().toString(),
          role: 'assistant' as const,
          content: aiContent || "Let me ask a few questions to build exactly what you want:",
          questions: result.questions
        }
        
        setMessages(prev => [...prev, newMessage])
        // Also track phase-specific messages
        setPhaseMessages(prev => ({
          ...prev,
          [currentPhase]: [...(prev[currentPhase] || []), newMessage]
        }))
        setCurrentQuestions(result.questions || [])
        
      } else if (result.status === 'ready_for_validation') {
        setContestPlan(result.contestPlan)
        setStep(3)
        // Set all phases as confirmed and go to Review
        setPhaseConfirmed({1: true, 2: true, 3: true, 4: true})
        setCurrentPhase(5)
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          role: 'assistant',
          content: "✅ I've built your contest based on verified data! Review everything below before creating:",
          contestPlan: result.contestPlan,
          searchResults: result.searchResults || result.gameAnalysis,
          validationPlan: result.validationPlan || result.contestPlan?.validationPlan
        }])
        
      } else if (result.status === 'rejected') {
        // Handle rejection (game not found, past games, etc.)
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          role: 'assistant',
          content: `❌ **Can't create this contest**\n\n${result.issue || result.reason || 'Unknown issue'}\n\n💡 **Suggestion:** ${result.suggestion || 'Try a different game or date.'}`
        }])
        setStep(1)
        
      } else {
        // Handle unexpected status
        console.error('Unexpected status:', result.status, result)
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          role: 'assistant',
          content: result.interpretation || result.message || "I received your request. Let me process it...",
          questions: result.questions
        }])
        if (result.questions) {
          setCurrentQuestions(result.questions)
        }
      }
      
    } catch (error: any) {
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'assistant',
        content: `Sorry, I had trouble with that. ${error.response?.data?.error || 'Please try again.'}`
      }])
    } finally {
      setIsLoading(false)
      // NOW clear answers - after loading is done and new questions are shown
      setAnswers({})
    }
  }
  
  // ============================================================================
  // CONFIRM CURRENT PHASE & ADVANCE
  // ============================================================================
  
  const confirmCurrentPhase = () => {
    if (currentPhase < 5) {
      setPhaseConfirmed(prev => ({ ...prev, [currentPhase]: true }))
      setCurrentPhase((currentPhase + 1) as 1|2|3|4|5)
      // Auto-send to get next phase questions
      sendMessage(`Continue to ${phases[currentPhase]?.name || 'next'} phase`)
    }
  }
  
  const goToPhase = (phase: 1|2|3|4|5) => {
    // Can only go to confirmed phases or the current phase
    if (phaseConfirmed[phase] || phase === currentPhase || (phase === 1)) {
      setCurrentPhase(phase)
    }
  }
  
  // ============================================================================
  // CREATE CONTEST
  // ============================================================================
  
  const createContest = async () => {
    if (!contestPlan) return
    setIsLoading(true)
    
    try {
      const response = await axios.post(`${API_BASE_URL}/api/fantasy/create`, {
        contestPlan,
        creatorId: user?.id || 1
      }, {
        timeout: 36000000 // 10 hours timeout
      })
      
      if (response.data.success) {
        const contestId = response.data.contestId
        toast.success('Contest created successfully!')
        
        // Show success message with link to contest
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          role: 'assistant',
          content: `🎉 **Contest Created!**\n\nYour contest "${contestPlan.title}" is now live with **${response.data.playerCount} real NFL players**!\n\n➡️ Redirecting you to the contest page where you and others can enter...`
        }])
        
        // Navigate to the contest page after a brief delay so user sees the success message
        setTimeout(() => {
          navigate(`/fantasy/${contestId}`)
        }, 2000)
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create contest')
    } finally {
      setIsLoading(false)
    }
  }
  
  // ============================================================================
  // RENDER
  // ============================================================================
  
  return (
    <div className="min-h-screen py-12 relative overflow-hidden">
      {/* Background decorative blobs - matches Create Pool page */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-40 right-20 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 left-20 w-80 h-80 bg-green-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back to Landing + Mode Indicator */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate('/fantasy')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 rounded-lg border border-slate-700">
            <Settings className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-400">Custom Builder</span>
          </div>
        </div>
        
        {/* Hero Header - matches Create Pool page */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-6xl font-black text-white mb-4">
              🔧 Custom <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-green-500">Fantasy</span> Builder
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-4">
              Full control over every setting. Chat with AI to build exactly what you want.
            </p>
            <p className="text-sm text-gray-500">
              Want something faster? <button onClick={() => navigate('/fantasy/quick')} className="text-emerald-400 hover:text-emerald-300 underline">Try Quick Build</button>
            </p>
          </motion.div>
        </div>

        {/* 5-Phase Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-center mb-4">
            {phases.map((phase, idx) => (
              <div key={phase.num} className="flex items-center">
                <button
                  onClick={() => phaseConfirmed[phase.num] || currentPhase === phase.num ? setCurrentPhase(phase.num as 1|2|3|4|5) : null}
                  disabled={!phaseConfirmed[phase.num] && currentPhase !== phase.num && phase.num !== 1}
                  className={`
                    w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold shadow-lg transition-all duration-300 cursor-pointer
                    ${phaseConfirmed[phase.num] 
                      ? 'bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-emerald-500/50 ring-2 ring-emerald-400/50' 
                      : currentPhase === phase.num
                        ? 'bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-amber-500/50 ring-2 ring-amber-400/50 animate-pulse'
                        : 'bg-slate-800 border-2 border-slate-700 text-gray-500 cursor-not-allowed'
                    }
                  `}
                >
                  {phaseConfirmed[phase.num] ? '✓' : phase.icon}
                </button>
                {idx < phases.length - 1 && (
                  <div className={`
                    w-8 md:w-12 h-1 mx-1 rounded-full transition-all duration-300
                    ${phaseConfirmed[phase.num] ? 'bg-gradient-to-r from-emerald-500 to-green-500' : 'bg-slate-700'}
                  `} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center">
            <div className="text-center">
              <span className={`text-lg font-bold ${currentPhase === 5 ? 'text-emerald-400' : 'text-amber-400'}`}>
                {phases[currentPhase - 1]?.icon || '📋'} {phases[currentPhase - 1]?.name || 'Contest'}
              </span>
              <p className="text-sm text-gray-400 mt-1">{phases[currentPhase - 1]?.description || ''}</p>
            </div>
          </div>
        </div>
        
        {/* Main Card - gradient border, MUCH bigger */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 via-green-500/10 to-emerald-500/20 rounded-2xl blur-xl"></div>
          <div className="relative bg-slate-800/90 backdrop-blur-sm rounded-2xl border-2 border-emerald-500/30 shadow-2xl shadow-emerald-500/10" style={{ minHeight: messages.length > 0 ? '700px' : 'auto' }}>
            
            {/* Initial Setup - Contest Type + Games */}
            {step === 1 && messages.length === 0 && (
              <div className="p-6 md:p-8">
                {/* Contest Type Selection - Compact inline */}
                <div className="max-w-4xl mx-auto mb-6">
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="text-gray-400 font-medium">First, pick your style:</span>
                    
                    <button
                      onClick={() => setContestType('survivor')}
                      className={`px-4 py-2 rounded-lg border-2 transition-all flex items-center gap-2 ${
                        contestType === 'survivor'
                          ? 'bg-amber-500/20 border-amber-500 text-amber-300'
                          : 'bg-slate-800/50 border-slate-600 hover:border-amber-500/50 text-gray-400'
                      }`}
                    >
                      🏆 Pick TEAMS
                      <span className="text-xs opacity-70">(Survivor, Pick'em)</span>
                    </button>
                    
                    <button
                      onClick={() => setContestType('dfs')}
                      className={`px-4 py-2 rounded-lg border-2 transition-all flex items-center gap-2 ${
                        contestType === 'dfs'
                          ? 'bg-emerald-500/20 border-emerald-500 text-emerald-300'
                          : 'bg-slate-800/50 border-slate-600 hover:border-emerald-500/50 text-gray-400'
                      }`}
                    >
                      👥 Pick PLAYERS
                      <span className="text-xs opacity-70">(DFS, Roster)</span>
                    </button>
                    
                    {!contestType && <span className="text-amber-400 text-sm animate-pulse">← Pick one</span>}
                  </div>
                </div>
                
                {/* PHASE 1: WHICH NFL GAMES? */}
                {contestType && (
                  <div className="max-w-4xl mx-auto">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">🏈</span>
                      <h3 className="text-lg font-bold text-white">Which NFL games?</h3>
                    </div>
                    
                    <input
                      ref={inputRef}
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !isLoading && contestType && sendMessage()}
                      placeholder={
                        contestType === 'survivor'
                          ? "e.g., 'Next 3 Bucs games' or 'Week 15 games' or 'Rest of the season'"
                          : "e.g., 'Chiefs vs Bills' or 'Christmas Day games' or 'Week 15 Sunday slate'"
                      }
                      className="w-full px-6 py-5 bg-slate-900/50 border-2 border-slate-600 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl text-white placeholder-gray-500 text-xl transition-all"
                    />
                    
                    {/* Quick game options */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <span className="text-sm text-gray-500">Quick picks:</span>
                      {[
                        { label: "This Week's Games", value: "This week's NFL games" },
                        { label: "Sunday Slate", value: "All Sunday NFL games this week" },
                        { label: "Monday Night", value: "Monday Night Football" },
                        { label: "Thursday Night", value: "Thursday Night Football" },
                        { label: "Rest of Season", value: "Rest of the NFL season" },
                      ].map((opt) => (
                        <button
                          key={opt.label}
                          onClick={() => setInput(opt.value)}
                          className={`px-3 py-1.5 text-sm rounded-lg border transition-all ${
                            contestType === 'survivor' 
                              ? 'border-amber-500/30 hover:border-amber-500 hover:bg-amber-500/10 text-amber-300'
                              : 'border-emerald-500/30 hover:border-emerald-500 hover:bg-emerald-500/10 text-emerald-300'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    
                    <p className="mt-3 text-xs text-gray-500 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      Just games now — scoring &amp; rules come next
                    </p>
                  </div>
                )}
                
                {/* Examples Dropdown - ONLY games examples now */}
                {contestType && (
                  <div className="max-w-4xl mx-auto mt-6">
                    <details className="group">
                      <summary className="flex items-center justify-between cursor-pointer p-4 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-slate-600 transition-all">
                        <div className="flex items-center gap-3">
                          <Search className={`w-5 h-5 ${contestType === 'survivor' ? 'text-amber-400' : 'text-emerald-400'}`} />
                          <span className="font-medium text-white">
                            Game Selection Examples
                          </span>
                          <span className="text-xs text-gray-500">Click for ideas</span>
                        </div>
                        <ChevronDown className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" />
                      </summary>
                      
                      <div className={`mt-2 p-4 rounded-xl border ${
                        contestType === 'survivor' 
                          ? 'bg-amber-900/20 border-amber-500/30' 
                          : 'bg-emerald-900/20 border-emerald-500/30'
                      }`}>
                        <div className="grid md:grid-cols-2 gap-3">
                          {/* GAME SELECTION EXAMPLES - Same for both types */}
                          {[
                            { display: "Next 3 Bucs games", icon: "🏈" },
                            { display: "Chiefs vs Bills this week", icon: "⚔️" },
                            { display: "Week 15 all games", icon: "📅" },
                            { display: "Christmas Day triple-header", icon: "🎄" },
                            { display: "Monday Night Football", icon: "🌙" },
                            { display: "Rest of the NFL season", icon: "🏆" },
                            { display: "Playoff games only", icon: "🎯" },
                            { display: "Lions vs Bills Sunday", icon: "🦁" }
                          ].map((ex, i) => (
                            <button
                              key={i}
                              onClick={() => { setInput(ex.display); setTimeout(() => inputRef.current?.focus(), 50); }}
                              className={`p-3 text-left text-sm bg-slate-800/50 border rounded-lg transition-all ${
                                contestType === 'survivor'
                                  ? 'border-amber-500/20 hover:border-amber-500/50 hover:bg-slate-800'
                                  : 'border-emerald-500/20 hover:border-emerald-500/50 hover:bg-slate-800'
                              }`}
                            >
                              <span className="mr-2">{ex.icon}</span>
                              <span className="text-gray-300">{ex.display}</span>
                            </button>
                          ))}
                        </div>
                        
                        <p className="mt-4 text-center text-xs text-gray-500">
                          💡 Just pick the games now - you'll set up scoring & rules in the next steps!
                        </p>
                      </div>
                    </details>
                  </div>
                )}
                
              </div>
            )}
            
            {/* TWO-COLUMN LAYOUT: Summary + Scrollable Chat */}
            {messages.length > 0 && (
              <div className="flex h-[600px]">
                
                {/* LEFT: AI Interpreted Summary - SCROLLABLE */}
                <div className="w-[260px] flex-shrink-0 bg-slate-900/95 border-r border-slate-700 lg:flex hidden flex-col">
                  <div className="p-4 border-b border-slate-700/50">
                    <h3 className="text-sm font-bold text-emerald-400 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Final Plan
                    </h3>
                    <p className="text-xxs text-gray-500">AI's interpretation of your choices</p>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {(() => {
                      const planMsg = [...messages].reverse().find(m => m.role === 'assistant' && m.contestPlan);
                      const plan = planMsg?.contestPlan;
                      
                      if (plan) {
                        const rows: { label: string; value?: any }[] = [
                          { label: 'Games', value: plan.gameSelection?.games?.join(', ') || plan.gameSelection?.type },
                          { label: 'Scoring', value: plan.scoring?.description || plan.teamScoring?.description },
                          { label: 'Locks', value: plan.locking || plan.locks },
                          { label: 'Entry Fee', value: plan.entryFee ? `$${plan.entryFee}` : undefined },
                          { label: 'Prizes', value: plan.prizeStructure?.description },
                          { label: 'Title', value: plan.title || plan.name },
                        ];
                        
                        return rows
                          .filter(r => r.value)
                          .map((row, i) => (
                            <div key={i} className="p-2 bg-slate-800/50 rounded-lg border border-slate-700/50">
                              <div className="text-xxs text-gray-500">{row.label}</div>
                              <div className="text-xs text-emerald-300 break-words whitespace-normal">{String(row.value)}</div>
                            </div>
                          ));
                      }
                      
                      // Fallback: show AI INTERPRETED summary (not raw user answers!)
                      // This shows the AI's understanding, not verbatim user text
                      const summaryEntries = Object.entries(aiInterpretedSummary);
                      
                      if (summaryEntries.length > 0) {
                        return summaryEntries.map(([label, value]) => (
                          <div key={label} className="p-2 bg-slate-800/50 rounded-lg border border-slate-700/50">
                              <div className="text-xxs text-gray-500">{label}</div>
                              <div className="text-xs text-emerald-300 break-words whitespace-normal">{String(value)}</div>
                            </div>
                        ));
                      }
                      
                      return (
                        <div className="text-xs text-gray-500 italic">
                          AI summary will appear as the plan takes shape...
                        </div>
                      );
                    })()}
                  </div>
                </div>
                
                {/* RIGHT: Chat Area with input at bottom */}
                <div className="flex-1 flex flex-col">
                  {/* Scrollable chat content */}
                  <div className="flex-1 overflow-y-auto p-6">
                  {/* Phase Header - stays at top of scroll area */}
                  <div className={`mb-6 p-4 rounded-xl ${
                    currentPhase === 5 
                      ? 'bg-emerald-500/10 border border-emerald-500/30' 
                      : 'bg-amber-500/10 border border-amber-500/30'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{phases[currentPhase - 1]?.icon || '📋'}</span>
                        <div>
                          <h3 className={`text-lg font-bold ${currentPhase === 5 ? 'text-emerald-300' : 'text-amber-300'}`}>
                            Step {currentPhase}: {phases[currentPhase - 1]?.name || 'Review'}
                          </h3>
                          <p className="text-sm text-gray-400">{phases[currentPhase - 1]?.description || 'Review your contest'}</p>
                        </div>
                      </div>
                      {currentPhase < 5 && !isLoading && currentQuestions.length === 0 && (
                        <button
                          onClick={confirmCurrentPhase}
                          className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium rounded-lg hover:from-amber-400 hover:to-orange-400 transition-all flex items-center gap-2 text-sm"
                        >
                          ✓ Next Step
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* ONLY show the LATEST message with questions (clean slate approach) */}
                  {(() => {
                    // Find the last assistant message with questions
                    const lastMsgWithQuestions = [...messages].reverse().find(m => m.role === 'assistant' && m.questions && m.questions.length > 0);
                    const lastMsg = messages[messages.length - 1];
                    
                    if (lastMsg?.contestPlan) {
                      return (
                        <ContestPlanCard 
                          plan={lastMsg.contestPlan}
                          searchResults={lastMsg.searchResults}
                          validationPlan={lastMsg.validationPlan}
                          onConfirm={createContest}
                          isLoading={isLoading}
                        />
                      );
                    }
                    
                    if (lastMsgWithQuestions) {
                      return (
                        <div className="space-y-4">
                          {/* AI Message */}
                          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                            <div className="flex items-center gap-2 mb-2">
                              <Brain className="w-4 h-4 text-emerald-400" />
                              <span className="text-xs text-emerald-400 font-medium">AI Assistant</span>
                            </div>
                            <div className="whitespace-pre-wrap text-sm text-gray-200">{lastMsgWithQuestions.content}</div>
                          </div>
                          
                          {/* Questions */}
                          <div className="space-y-4">
                            {lastMsgWithQuestions.questions?.map((q, idx) => (
                              <QuestionCard
                                key={idx}
                                question={q}
                                value={answers[q.category || q.question] || ''}
                                onChange={(value) => setAnswers(prev => ({
                                  ...prev,
                                  [q.category || q.question]: value
                                }))}
                                disabled={isLoading}
                              />
                            ))}
                            
                            {/* Submit */}
                            <button
                              onClick={() => sendMessage()}
                              disabled={isLoading}
                              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-bold rounded-xl hover:from-emerald-400 hover:to-green-500 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                            >
                              {isLoading ? (
                                <Loader className="w-5 h-5 animate-spin" />
                              ) : (
                                <>Submit & Continue <ChevronRight className="w-4 h-4" /></>
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    }
                    
                    return null;
                  })()}
                  
                  {/* Loading */}
                  {isLoading && (
                    <div className="flex items-center justify-center py-12">
                      <div className="bg-slate-800/50 rounded-xl px-6 py-4 border border-slate-700 flex items-center gap-3">
                        <Loader className="w-5 h-5 animate-spin text-emerald-400" />
                        <span className="text-gray-300">AI is thinking...</span>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                  </div>
                  
                  {/* Input Footer - INSIDE the right column, at bottom */}
                  {!contestPlan && (
                    <div className="p-4 border-t border-slate-700/50 bg-slate-900/50 flex-shrink-0">
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && !isLoading && sendMessage()}
                          placeholder="Type a message or describe changes..."
                          disabled={isLoading}
                          className="flex-1 px-4 py-3 bg-slate-800/50 border border-slate-600 rounded-xl text-white placeholder-gray-500 focus:border-emerald-500 disabled:opacity-50 text-sm"
                        />
                        <button
                          onClick={() => sendMessage()}
                          disabled={isLoading || (!input.trim() && Object.keys(answers).length === 0)}
                          className="px-5 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                          <Send className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Info Cards */}
        {messages.length === 0 && (
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <InfoCard
              icon={<Target className="w-6 h-6 text-orange-400" />}
              title="Single Game"
              description="Pick players from one matchup. Captain earns 1.5x. Resolves in ~3 hours."
            />
            <InfoCard
              icon={<Users className="w-6 h-6 text-purple-400" />}
              title="Multi-Game"
              description="Select 2-5 specific games. Build your perfect slate."
            />
            <InfoCard
              icon={<Trophy className="w-6 h-6 text-emerald-400" />}
              title="Full Week"
              description="All games in one NFL week. Classic DFS format."
            />
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function StepBadge({ number, label, active, completed }: { number: number; label: string; active: boolean; completed: boolean }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
      completed ? 'bg-emerald-500/20 text-emerald-400' :
      active ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' :
      'bg-slate-800 text-gray-500'
    }`}>
      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
        completed ? 'bg-emerald-500 text-white' :
        active ? 'bg-emerald-500/20 text-emerald-400' :
        'bg-slate-700 text-gray-500'
      }`}>
        {completed ? <CheckCircle className="w-3 h-3" /> : number}
      </span>
      <span className="text-xs font-medium">{label}</span>
    </div>
  )
}

function InfoCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50 hover:border-slate-600 transition-colors">
      <div className="mb-3">{icon}</div>
      <h3 className="font-semibold text-white mb-1">{title}</h3>
      <p className="text-sm text-gray-400">{description}</p>
    </div>
  )
}

function QuestionCard({ question, value, onChange, disabled = false }: { 
  question: Question
  value: string
  onChange: (value: string) => void
  disabled?: boolean 
}) {
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  
  // Get options first so we can check if value is custom
  const options = question.suggestions || question.options || [];
  
  // Check if the current value is NOT one of the preset options (meaning user typed custom)
  const isCustomValue = value && !options.includes(value) && value !== '__custom__';
  
  // Reset state when question changes, but preserve custom input state if value is custom
  useEffect(() => {
    // If current value is custom (not in options), show custom input
    if (isCustomValue) {
      setShowCustomInput(true);
      setCustomValue(value);
    } else {
      setShowCustomInput(false);
      setCustomValue('');
    }
    setIsOpen(false);
  }, [question.question, question.category]);
  
  // Check if this is the rolling lock question - highlight it!
  const isRollingLock = question.category === 'rolling_lock' || 
                        question.question?.toLowerCase().includes('rolling') ||
                        (question as any).highlight;
  
  // Always inject a Custom option at the end if none present
  const hasCustomOption = options.some(opt => 
    opt.toLowerCase().includes('custom') || 
    opt.toLowerCase().includes("i'll specify") || 
    opt.toLowerCase().includes("i'll describe")
  );
  const optionsWithCustom = hasCustomOption
    ? options
    : [...options, "✏️ Custom - I'll type my own..."];

  // Filter out custom options from main list
  const mainOptions = optionsWithCustom.filter(opt => 
    !opt.toLowerCase().includes('custom') && 
    !opt.toLowerCase().includes("i'll specify") && 
    !opt.toLowerCase().includes("i'll describe")
  );
  
  // Handle option selection
  const handleOptionSelect = (selectedValue: string) => {
    if (selectedValue === '__custom__') {
      setShowCustomInput(true);
      setCustomValue('');
      setIsOpen(false);
    } else {
      setShowCustomInput(false);
      setCustomValue('');
      onChange(selectedValue);
      setIsOpen(false);
    }
  };
  
  // Handle custom input change
  const handleCustomInputChange = (newValue: string) => {
    setCustomValue(newValue);
    if (newValue.trim()) {
      onChange(newValue);
    }
  };

  // Get display text for selected value
  const getDisplayText = () => {
    if (showCustomInput) return '✏️ Custom - I\'ll type my own...';
    if (isCustomValue) return value.slice(0, 60) + (value.length > 60 ? '...' : '');
    if (value) return value.slice(0, 60) + (value.length > 60 ? '...' : '');
    return 'Select an option...';
  };
  
  return (
    <div className={`rounded-xl p-4 border ${
      isRollingLock 
        ? 'bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border-yellow-500/50 ring-2 ring-yellow-500/20' 
        : 'bg-slate-800/50 border-slate-700/50'
    }`}>
      {/* Special banner for rolling lock */}
      {isRollingLock && (
        <div className="flex items-center gap-2 mb-3 text-yellow-400 text-xs font-semibold">
          <span className="animate-pulse">🔄</span>
          <span>MULTI-GAME FEATURE</span>
          <span className="animate-pulse">⭐</span>
        </div>
      )}
      
      <label className={`block text-sm font-medium mb-3 ${
        isRollingLock ? 'text-yellow-200' : 'text-gray-300'
      }`}>
        {question.question}
        {question.required && <span className="text-red-400 ml-1">*</span>}
      </label>
      
      {/* Description if provided */}
      {(question as any).description && (
        <p className="text-xs text-gray-400 mb-3 italic">{(question as any).description}</p>
      )}
      
      {/* CUSTOM DROPDOWN - Supports text wrapping! */}
      {options.length > 0 && (
        <div className="mb-3 relative">
          {/* Dropdown trigger button - DISABLED when loading */}
          {disabled ? (
            /* Read-only display when disabled/loading */
            <div className={`w-full px-4 py-3 rounded-lg text-sm text-left flex items-center justify-between ${
              value 
                ? 'bg-emerald-900/30 border-emerald-500/30 text-emerald-300' 
                : 'bg-slate-900/50 border-slate-600 text-gray-500'
            } border opacity-75`}>
              <span className="truncate pr-2">{getDisplayText()}</span>
              {value && <CheckCircle className="w-4 h-4 flex-shrink-0 text-emerald-400" />}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className={`w-full px-4 py-3 rounded-lg text-sm text-left transition-all cursor-pointer flex items-center justify-between ${
                isRollingLock 
                  ? 'bg-yellow-900/30 border-yellow-500/50 text-yellow-200 hover:border-yellow-400'
                  : value 
                    ? 'bg-emerald-900/20 border-emerald-500/50 text-emerald-300 hover:border-emerald-400' 
                    : 'bg-slate-900/50 border-slate-600 text-gray-400 hover:border-slate-500'
              } border`}
            >
              <span className="truncate pr-2">{getDisplayText()}</span>
              <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
          )}
          
          {/* Dropdown options - WITH TEXT WRAPPING - Only show if not disabled */}
          {/* 🚀 FIX: Open UPWARDS because we're at the bottom of the chat! */}
          {isOpen && !disabled && (
            <div className="absolute z-50 w-full bottom-full mb-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-60 overflow-y-auto">
              {mainOptions.map((option, i) => {
                const isSelected = value === option;
                const isRecommended = option.toLowerCase().includes('recommended') || option.includes('✅');
                
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleOptionSelect(option)}
                    className={`w-full px-4 py-3 text-left text-sm transition-all border-b border-slate-700/50 last:border-b-0 ${
                      isSelected
                        ? 'bg-emerald-600/20 text-emerald-300'
                        : isRecommended
                          ? 'bg-yellow-900/10 text-yellow-200 hover:bg-yellow-900/20'
                          : 'text-gray-300 hover:bg-slate-700/50'
                    }`}
                  >
                    {/* Text wraps naturally here! */}
                    <span className="whitespace-normal break-words">
                      {isRecommended && !option.includes('⭐') ? '⭐ ' : ''}{option}
                    </span>
                  </button>
                );
              })}
              
              {/* Custom option */}
              {hasCustomOption && (
                <button
                  type="button"
                  onClick={() => handleOptionSelect('__custom__')}
                  className={`w-full px-4 py-3 text-left text-sm transition-all ${
                    showCustomInput
                      ? 'bg-emerald-600/20 text-emerald-300'
                      : 'text-emerald-400 hover:bg-slate-700/50'
                  }`}
                >
                  ✏️ Custom - I'll type my own...
                </button>
              )}
            </div>
          )}
          
          {/* Click outside to close */}
          {isOpen && (
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsOpen(false)}
            />
          )}
        </div>
      )}
      
      {/* Custom input field - shows when: 
          1. Explicit text_input type, OR
          2. User selected "Custom" option, OR
          3. No options provided (free text)
      */}
      {(question.type === 'text_input' || 
        question.type === 'text_with_suggestions' || 
        showCustomInput ||
        options.length === 0) && (
        <div className="mt-3">
          {showCustomInput && (
            <label className="block text-xs text-emerald-400 mb-2 font-medium">
              ✏️ Enter your custom value:
            </label>
          )}
          <input
            type="text"
            value={showCustomInput ? customValue : (isCustomValue ? value : '')}
            onChange={(e) => showCustomInput ? handleCustomInputChange(e.target.value) : onChange(e.target.value)}
            placeholder={question.placeholder || 'Type your custom value...'}
            className={`w-full px-4 py-3 bg-slate-900/50 border rounded-lg text-white text-sm placeholder-gray-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all ${
              showCustomInput || isCustomValue ? 'border-emerald-500/50' : 'border-slate-600'
            }`}
          />
          {question.placeholder && !showCustomInput && options.length === 0 && (
            <p className="text-xs text-gray-500 mt-1 italic">{question.placeholder}</p>
          )}
        </div>
      )}
      
      {/* Show current custom value if set */}
      {isCustomValue && !showCustomInput && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs text-emerald-400">✓ Custom:</span>
          <span className="text-xs text-white bg-emerald-500/20 px-2 py-1 rounded">{value}</span>
        </div>
      )}
    </div>
  )
}

function ContestPlanCard({ plan, searchResults, validationPlan, onConfirm, isLoading }: { 
  plan: any
  searchResults?: any
  validationPlan?: any
  onConfirm: () => void
  isLoading: boolean
}) {
  const [showDetails, setShowDetails] = useState(false)
  
  return (
    <div className="mt-4 bg-slate-800/80 rounded-xl border-2 border-emerald-500/30 overflow-hidden">
      {/* Header */}
      <div className="bg-emerald-600/20 px-5 py-4 border-b border-emerald-500/30">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="font-bold text-white text-lg">{plan.title}</h3>
            <p className="text-sm text-gray-400">{plan.description}</p>
          </div>
        </div>
      </div>
      
      {/* Search Results - What Perplexity Found */}
      {searchResults?.gamesFound && searchResults.gamesFound.length > 0 && (
        <div className="px-5 py-4 bg-blue-500/10 border-b border-blue-500/20">
          <div className="flex items-center gap-2 mb-3">
            <Search className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-medium text-blue-300">🔍 Verified Games (from live search)</span>
          </div>
          <div className="space-y-2">
            {searchResults.gamesFound.map((game: any, i: number) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                <span className="text-white">
                  {game.awayTeam} @ {game.homeTeam}
                </span>
                <span className="text-gray-400">•</span>
                <span className="text-gray-400">
                  {game.gameDate} at {game.gameTime}
                </span>
                <span className={`px-2 py-0.5 rounded text-xs ${
                  game.status === 'SCHEDULED' ? 'bg-green-500/20 text-green-400' :
                  game.status === 'LIVE' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {game.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Details */}
      <div className="p-5 space-y-4">
        {plan.gameSelection && (
          <DetailRow 
            icon={<Zap className="w-4 h-4 text-orange-400" />}
            label="Games"
            value={plan.gameSelection.games?.join(', ') || plan.gameSelection.type}
          />
        )}
        
        {plan.scoring && (
          <>
            <DetailRow 
              icon={<Target className="w-4 h-4 text-yellow-400" />}
              label="Scoring"
              value={plan.scoring.description}
            />
            
            {/* 🚨 FORMULA-BASED SCORING (Quadratic, custom formulas, etc) */}
            {plan.scoring.formulas && plan.scoring.formulas.length > 0 && (
              <div className="mt-3 p-4 bg-purple-500/5 rounded-xl border border-purple-500/20">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">🧮</span>
                  <span className="font-semibold text-purple-300">
                    {plan.scoring.name || 'Custom Formula Scoring'}
                  </span>
                </div>
                
                {plan.scoring.description && (
                  <p className="text-gray-400 text-sm mb-4">{plan.scoring.description}</p>
                )}
                
                {/* Formula List */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {plan.scoring.formulas.map((formula: any, i: number) => (
                    <div key={i} className="flex items-center justify-between bg-slate-800/50 px-3 py-2 rounded-lg">
                      <span className="text-gray-400 text-sm">{formula.name}</span>
                      <span className="font-mono text-sm text-purple-400">
                        {formula.description || formula.formula}
                      </span>
                    </div>
                  ))}
                </div>
                
                {/* Example - use AI-generated example if available */}
                {plan.scoring.example && (
                  <div className="p-3 bg-slate-900/70 rounded-lg border border-slate-700">
                    <div className="text-xs text-gray-400 mb-2">📝 Example Calculation:</div>
                    <p className="text-sm text-white whitespace-pre-wrap">{plan.scoring.example}</p>
                  </div>
                )}
                
                {/* Warning for quadratic/exponential scoring */}
                {plan.scoring.formulas.some((f: any) => f.formula?.includes('^') || f.formula?.includes('**')) && (
                  <div className="mt-3 flex items-start gap-2 text-xs text-purple-400 bg-purple-500/10 p-2 rounded">
                    <span>🚀</span>
                    <span>Exponential scoring! Big games are rewarded dramatically more than average ones.</span>
                  </div>
                )}
              </div>
            )}
            
            {/* 🚨 LEGACY RULES-BASED SCORING (multiplier per stat) */}
            {plan.scoring.rules && plan.scoring.rules.length > 0 && !plan.scoring.formulas?.length && (
              <div className="mt-3 p-4 bg-yellow-500/5 rounded-xl border border-yellow-500/20">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">📊</span>
                  <span className="font-semibold text-yellow-300">Scoring Breakdown</span>
                </div>
                
                {/* Scoring Rules */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {plan.scoring.rules.map((rule: any, i: number) => (
                    <div key={i} className="flex items-center justify-between bg-slate-800/50 px-3 py-2 rounded-lg">
                      <span className="text-gray-400 text-sm">{rule.stat || rule.description?.split(':')[0]}</span>
                      <span className={`font-mono font-bold ${
                        (rule.points || 0) >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {(rule.points || 0) >= 0 ? '+' : ''}{rule.points} pts
                      </span>
                    </div>
                  ))}
                </div>
                
                {/* Math Example for rules-based */}
                <div className="p-3 bg-slate-900/70 rounded-lg border border-slate-700">
                  <div className="text-xs text-gray-400 mb-2">📝 Example Calculation:</div>
                  <div className="text-sm text-white space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-400">QB throws 300 yards:</span>
                      <span className="text-green-400 font-mono">
                        300 × {plan.scoring.rules.find((r: any) => r.stat?.toLowerCase().includes('passing') && r.stat?.toLowerCase().includes('yard'))?.points || 0.04} = {(300 * (plan.scoring.rules.find((r: any) => r.stat?.toLowerCase().includes('passing') && r.stat?.toLowerCase().includes('yard'))?.points || 0.04)).toFixed(1)} pts
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">+ 2 passing TDs:</span>
                      <span className="text-green-400 font-mono">
                        2 × {plan.scoring.rules.find((r: any) => r.stat?.toLowerCase().includes('passing') && r.stat?.toLowerCase().includes('td'))?.points || 4} = {(2 * (plan.scoring.rules.find((r: any) => r.stat?.toLowerCase().includes('passing') && r.stat?.toLowerCase().includes('td'))?.points || 4)).toFixed(1)} pts
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">+ 1 interception:</span>
                      <span className="text-red-400 font-mono">
                        1 × {plan.scoring.rules.find((r: any) => r.stat?.toLowerCase().includes('int'))?.points || -1} = {plan.scoring.rules.find((r: any) => r.stat?.toLowerCase().includes('int'))?.points || -1} pts
                      </span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-slate-700 mt-2">
                      <span className="text-white font-medium">= Total QB Points:</span>
                      <span className="text-yellow-400 font-mono font-bold">
                        {(
                          (300 * (plan.scoring.rules.find((r: any) => r.stat?.toLowerCase().includes('passing') && r.stat?.toLowerCase().includes('yard'))?.points || 0.04)) +
                          (2 * (plan.scoring.rules.find((r: any) => r.stat?.toLowerCase().includes('passing') && r.stat?.toLowerCase().includes('td'))?.points || 4)) +
                          (plan.scoring.rules.find((r: any) => r.stat?.toLowerCase().includes('int'))?.points || -1)
                        ).toFixed(1)} pts
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* High Scoring Warning if applicable */}
                {plan.scoring.rules.some((r: any) => r.stat?.toLowerCase().includes('yard') && r.points >= 0.5) && (
                  <div className="mt-3 flex items-start gap-2 text-xs text-orange-400 bg-orange-500/10 p-2 rounded">
                    <span>⚠️</span>
                    <span>High-scoring format! With 1 pt per yard, expect scores of 500+ per player.</span>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        
        {/* 🏆 TEAM SCORING - For Survivor/Pick'em contests */}
        {plan.teamScoring && (
          <div className="p-4 bg-amber-500/5 rounded-xl border border-amber-500/20">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">🏆</span>
              <span className="font-semibold text-amber-300">Team Scoring System</span>
            </div>
            
            <p className="text-sm text-gray-300 mb-4">{plan.teamScoring.description}</p>
            
            {/* Team Scoring Rules */}
            {plan.teamScoring.rules && plan.teamScoring.rules.length > 0 && (
              <div className="grid grid-cols-1 gap-2 mb-4">
                {plan.teamScoring.rules.map((rule: any, i: number) => (
                  <div key={i} className="flex items-center justify-between bg-slate-800/50 px-4 py-3 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-amber-400">📊</span>
                      <span className="text-white font-medium">{rule.stat}</span>
                    </div>
                    <span className="text-amber-400 font-mono font-bold">
                      {rule.multiplier || rule.points || 1} pt{(rule.multiplier || rule.points || 1) !== 1 ? 's' : ''}/unit
                    </span>
                  </div>
                ))}
              </div>
            )}
            
            {/* Formula if present */}
            {plan.teamScoring.formula && (
              <div className="p-3 bg-slate-900/70 rounded-lg border border-slate-700 mb-4">
                <div className="text-xs text-gray-400 mb-1">Formula:</div>
                <code className="text-amber-400 font-mono text-sm">{plan.teamScoring.formula}</code>
              </div>
            )}
            
            {/* Team Scoring Example */}
            <div className="p-3 bg-slate-900/70 rounded-lg border border-slate-700">
              <div className="text-xs text-gray-400 mb-2">📝 Example Calculation:</div>
              <div className="text-sm text-white space-y-2">
                {plan.teamScoring.example ? (
                  <div className="text-gray-300">{plan.teamScoring.example}</div>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-400">If Bucs get 200 rushing yards:</span>
                      <span className="text-green-400 font-mono">
                        200 × {plan.teamScoring.rules?.find((r: any) => r.stat?.toLowerCase().includes('rushing'))?.multiplier || 1} = {200 * (plan.teamScoring.rules?.find((r: any) => r.stat?.toLowerCase().includes('rushing'))?.multiplier || 1)} pts
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">+ 250 passing yards:</span>
                      <span className="text-green-400 font-mono">
                        250 × {plan.teamScoring.rules?.find((r: any) => r.stat?.toLowerCase().includes('passing'))?.multiplier || 1} = {250 * (plan.teamScoring.rules?.find((r: any) => r.stat?.toLowerCase().includes('passing'))?.multiplier || 1)} pts
                      </span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-slate-700 mt-2">
                      <span className="text-white font-medium">= Your points for this game:</span>
                      <span className="text-amber-400 font-mono font-bold">
                        {(200 * (plan.teamScoring.rules?.find((r: any) => r.stat?.toLowerCase().includes('rushing'))?.multiplier || 1)) + 
                         (250 * (plan.teamScoring.rules?.find((r: any) => r.stat?.toLowerCase().includes('passing'))?.multiplier || 1))} pts
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {/* Bonus Thresholds if present */}
            {plan.teamScoring.bonuses && plan.teamScoring.bonuses.length > 0 && (
              <div className="mt-4 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                <div className="text-xs text-green-400 font-medium mb-2">🎯 Bonus Thresholds:</div>
                <div className="space-y-1">
                  {plan.teamScoring.bonuses.map((bonus: any, i: number) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-gray-300">{bonus.threshold}+ yards:</span>
                      <span className="text-green-400 font-mono">+{bonus.points} bonus pts</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {plan.rosterFormat && (
          <>
            <DetailRow 
              icon={<Users className="w-4 h-4 text-blue-400" />}
              label="Roster"
              value={plan.rosterFormat.description}
            />
            
            {/* Roster Position Breakdown */}
            {plan.rosterFormat.positions && (
              <div className="ml-8 flex flex-wrap gap-2">
                {Object.entries(plan.rosterFormat.positions).map(([pos, posData]) => {
                  // Handle both formats: simple number OR complex object {count, multiplier, description}
                  const count = typeof posData === 'object' && posData !== null 
                    ? (posData as any).count || 1 
                    : posData as number;
                  const multiplier = typeof posData === 'object' && posData !== null 
                    ? (posData as any).multiplier 
                    : null;
                  const desc = typeof posData === 'object' && posData !== null 
                    ? (posData as any).description 
                    : null;
                  
                  return (
                    <span key={pos} className="px-2 py-1 bg-blue-500/10 text-blue-300 text-xs rounded-lg border border-blue-500/20">
                      {count}× {pos}{multiplier && multiplier !== 1 ? ` (${multiplier}x)` : ''}{desc ? ` - ${desc}` : ''}
                    </span>
                  );
                })}
                <span className="px-2 py-1 bg-slate-700 text-gray-300 text-xs rounded-lg">
                  = {plan.rosterFormat.totalPlayers || Object.values(plan.rosterFormat.positions).reduce((a: any, b: any) => {
                    const countA = typeof a === 'object' ? (a as any).count || 0 : (typeof a === 'number' ? a : 0);
                    const countB = typeof b === 'object' ? (b as any).count || 0 : (typeof b === 'number' ? b : 0);
                    return countA + countB;
                  }, 0)} players
                </span>
              </div>
            )}
          </>
        )}
        
        {plan.salaryCap && (
          <>
            <DetailRow 
              icon={<DollarSign className="w-4 h-4 text-green-400" />}
              label="Budget"
              value={plan.salaryCap.enabled !== false 
                ? `$${(plan.salaryCap.amount || 50000).toLocaleString()} salary cap`
                : 'No salary cap - pick any players!'
              }
            />
            
            {/* Salary Cap Strategy */}
            {plan.salaryCap.amount && plan.salaryCap.amount > 0 && (
              <div className="ml-8 text-xs text-gray-400 bg-green-500/5 p-2 rounded-lg border border-green-500/10">
                💡 Avg per player: ~${Math.round((plan.salaryCap.amount || 50000) / (plan.rosterFormat?.totalPlayers || 6)).toLocaleString()}
                {plan.salaryCap.amount <= 40000 && ' (tight budget - choose wisely!)'}
              </div>
            )}
          </>
        )}
        
        {plan.timing && (
          <>
            <DetailRow 
              icon={<Clock className="w-4 h-4 text-purple-400" />}
              label="Roster Lock"
              value={plan.timing.lockTimingChoice 
                ? `${plan.timing.lockTimingChoice} • ${plan.timing.description}`
                : plan.timing.description}
            />
            {plan.timing.rollingLock && (
              <DetailRow 
                icon={<Zap className="w-4 h-4 text-yellow-400" />}
                label="Between Games"
                value="✏️ Roster unlocks between games - swap players, keep your points!"
              />
            )}
          </>
        )}
        
        {/* Entry Fee & Prize Structure */}
        {plan.entry && (
          <div className="space-y-2">
            <DetailRow 
              icon={<DollarSign className="w-4 h-4 text-pink-400" />}
              label="Entry Fee"
              value={
                plan.entry.entryFee === 0 || plan.entry.fee === 0 
                  ? '🎉 Free to play!' 
                  : `$${plan.entry.entryFee || plan.entry.fee || 0} per entry`
              }
            />
            <DetailRow 
              icon={<Trophy className="w-4 h-4 text-yellow-400" />}
              label="Prize Structure"
              value={
                plan.entry.prizePool?.description || 
                plan.entry.prizeStructure || 
                plan.entry.feeDescription ||
                'Winner takes all'
              }
            />
            {plan.entry.maxEntriesPerUser && (
              <DetailRow 
                icon={<Users className="w-4 h-4 text-blue-400" />}
                label="Max Entries"
                value={
                  plan.entry.maxEntriesPerUser === 1 
                    ? '1 entry per person (single entry)' 
                    : plan.entry.maxEntriesPerUser === null
                      ? 'Unlimited entries'
                      : `Up to ${plan.entry.maxEntriesPerUser} entries per person`
                }
              />
            )}
          </div>
        )}
        
        {/* Expandable Validation Plan */}
        {validationPlan && (
          <div className="pt-3 border-t border-slate-700/50">
            <button 
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
            >
              <Info className="w-4 h-4" />
              <span>{showDetails ? 'Hide' : 'Show'} how stats will be validated</span>
              <ChevronRight className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-90' : ''}`} />
            </button>
            
            {showDetails && (
              <div className="mt-3 p-4 bg-slate-900/50 rounded-lg text-xs space-y-3">
                <div>
                  <span className="text-gray-500">Strategy:</span>
                  <span className="text-gray-300 ml-2">{validationPlan.strategy || 'Post-game resolution'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Primary Tool:</span>
                  <span className="text-gray-300 ml-2">{validationPlan.primaryMethod?.api || 'Perplexity search'}</span>
                </div>
                <div>
                  <span className="text-gray-500">How it works:</span>
                  <div className="mt-1 text-gray-400">
                    For each player on your roster, we'll search for their official game stats after the game ends, 
                    then apply the scoring rules to calculate fantasy points.
                  </div>
                </div>
                {validationPlan.contingencies && (
                  <div>
                    <span className="text-gray-500">If something goes wrong:</span>
                    <ul className="mt-1 text-gray-400 space-y-1">
                      <li>• Game postponed: {validationPlan.contingencies.gamePostponed}</li>
                      <li>• Stats unavailable: {validationPlan.contingencies.statsUnavailable}</li>
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Review & Confirmation Section */}
      <div className="px-5 py-5 bg-amber-500/10 border-t border-amber-500/30">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
            <span className="text-xl">🔍</span>
          </div>
          <div>
            <h4 className="font-bold text-amber-300 text-lg">Final Review</h4>
            <p className="text-sm text-gray-400">Does everything look correct? You can make changes before creating.</p>
          </div>
        </div>
        
        {/* Quick Fix Buttons */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          <button className="px-3 py-2 bg-slate-800/80 hover:bg-amber-500/20 border border-slate-600 hover:border-amber-500/50 rounded-lg text-sm text-gray-300 hover:text-amber-300 transition-all flex items-center gap-2 justify-center">
            <span>🏈</span> Fix Games
          </button>
          <button className="px-3 py-2 bg-slate-800/80 hover:bg-amber-500/20 border border-slate-600 hover:border-amber-500/50 rounded-lg text-sm text-gray-300 hover:text-amber-300 transition-all flex items-center gap-2 justify-center">
            <span>📊</span> Fix Scoring
          </button>
          <button className="px-3 py-2 bg-slate-800/80 hover:bg-amber-500/20 border border-slate-600 hover:border-amber-500/50 rounded-lg text-sm text-gray-300 hover:text-amber-300 transition-all flex items-center gap-2 justify-center">
            <span>🏆</span> Fix Rules
          </button>
          <button className="px-3 py-2 bg-slate-800/80 hover:bg-amber-500/20 border border-slate-600 hover:border-amber-500/50 rounded-lg text-sm text-gray-300 hover:text-amber-300 transition-all flex items-center gap-2 justify-center">
            <span>🎨</span> Fix Theme
          </button>
        </div>
        
        <p className="text-xs text-gray-500 text-center mb-2">
          Use the chat below to discuss changes, or click a button above to jump to that section.
        </p>
      </div>
      
      {/* Create Contest Action */}
      <div className="px-5 py-4 bg-slate-900/50 border-t border-slate-700/50">
        <p className="text-xs text-gray-500 mb-3 text-center">
          By creating this contest, you confirm the games and scoring above are correct.
        </p>
        <button
          onClick={onConfirm}
          disabled={isLoading}
          className="w-full py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-bold rounded-xl hover:from-emerald-500 hover:to-green-500 disabled:opacity-50 transition-all flex items-center justify-center gap-2 text-lg"
        >
          {isLoading ? (
            <Loader className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Trophy className="w-5 h-5" />
              Create Contest
            </>
          )}
        </button>
      </div>
    </div>
  )
}

function DetailRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5">{icon}</div>
      <div>
        <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
        <div className="text-white text-sm">{value}</div>
      </div>
    </div>
  )
}
